import { createFileRoute, Link } from '@tanstack/react-router';
import {
  Badge,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Typography,
  Chip,
  Stack,
  CircularProgress,
  Grid,
  useTheme,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { visuallyHidden } from '@mui/utils';
import {
  ArrowForward as ArrowForwardIcon,
  FolderOpen as BinderIcon,
  Draw as DrawIcon,
  Description as TaxIcon,
} from '@mui/icons-material';
import { useTaxBinders } from '@/hooks/api/useTaxBinders';
import { useAuditDashboard } from '@/hooks/api/useAuditDashboard';
import { useClient } from '@/hooks/context/useClient';
import { useUnreadMessageCount } from '@/hooks/api/useUnreadMessageCount';
import { hasOutstandingSignatureHints } from '@/utils/eSignatureHelpers';
import { AuditBinderCard } from '@/components/binders/AuditBinderCard';
import { TaxBinderTypeChip } from '@/components/common/TaxBinderTypeChip';
import {
  AuditBinderStage,
  ServiceLine,
  type AuditDashboardItemDto,
  type TaxBinderDto,
} from '@ata-digital/client-tax-system-api-client';
import { SERVICE_LINE_CONFIG } from '@/components/common/ServiceLineBadges';
import { getClientStageLabel, getClientStageColor } from '@/features/tax-binders/utils/taxBinderDisplayUtils';

type CombinedBinder =
  | { kind: 'tax'; createdAt: number; binder: TaxBinderDto }
  | { kind: 'audit'; createdAt: number; binder: AuditDashboardItemDto };

export const Route = createFileRoute('/binders/')({
  // Optional `line` filter drives the Tax-only / Audit-only nav destinations.
  validateSearch: (search: Record<string, unknown>): { line?: 'tax' | 'audit' } => {
    const line = search.line;
    return line === 'tax' || line === 'audit' ? { line } : {};
  },
  component: BindersIndexPage,
});

function BindersIndexPage() {
  const theme = useTheme();
  const { line } = Route.useSearch();
  const { selectedClient } = useClient();
  const { data: taxResponse, isLoading: isTaxLoading } = useTaxBinders({}, { activeClientId: selectedClient?.id });
  // The audit dashboard defaults to "open work only" (it was built for the staff
  // dashboard), which would drop a binder from this page the moment staff completes
  // it. Clients need the completed binder to stay listed so they can download the
  // final deliverables, so request every client-visible stage explicitly. The backend
  // still enforces its own "Open or later" gate for client callers. This page has
  // no pagination, so request the backend's maximum page size (200) — the default
  // of 20 would silently drop binders once completed ones join the pool.
  const { data: auditResponse, isLoading: isAuditLoading } = useAuditDashboard(
    { stages: [AuditBinderStage.Open, AuditBinderStage.InReview, AuditBinderStage.Complete], limit: 200 },
    { activeClientId: selectedClient?.id }
  );
  const { perBinder } = useUnreadMessageCount();

  const taxBinders = taxResponse?.data?.items || [];
  const auditBinders = auditResponse?.data?.items || [];

  const combined: CombinedBinder[] = [
    ...taxBinders.map(
      (b): CombinedBinder => ({
        kind: 'tax',
        createdAt: b.createdAt ? new Date(b.createdAt).getTime() : 0,
        binder: b,
      })
    ),
    ...auditBinders.map(
      (b): CombinedBinder => ({
        kind: 'audit',
        createdAt: b.createdAt ? new Date(b.createdAt).getTime() : 0,
        binder: b,
      })
    ),
  ]
    .filter((entry) => !line || entry.kind === line)
    .sort((a, b) => b.createdAt - a.createdAt);

  const pageTitle = line === 'tax' ? 'Tax Binders' : line === 'audit' ? 'Audit Binders' : 'My Binders';

  const isLoading = isTaxLoading || isAuditLoading;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ py: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <BinderIcon />
        {pageTitle}
      </Typography>

      {combined.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <BinderIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No Binders Found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              You don't have any binders at the moment. Contact your preparer to get started.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {combined.map((entry) =>
            entry.kind === 'tax' ? (
              <Grid key={`tax-${entry.binder.id}`} size={{ xs: 12, md: 6 }}>
                <TaxBinderCard binder={entry.binder} perBinder={perBinder} theme={theme} />
              </Grid>
            ) : (
              <Grid key={`audit-${entry.binder.id}`} size={{ xs: 12, md: 6 }}>
                <AuditBinderCard binder={entry.binder} />
              </Grid>
            )
          )}
        </Grid>
      )}
    </Box>
  );
}

function TaxBinderCard({
  binder,
  perBinder,
  theme,
}: {
  binder: TaxBinderDto;
  perBinder: ReturnType<typeof useUnreadMessageCount>['perBinder'];
  theme: Theme;
}) {
  const unread = binder.id ? (perBinder.get(binder.id)?.unreadCount ?? 0) : 0;
  return (
    <Badge
      badgeContent={unread}
      color="error"
      max={99}
      overlap="rectangular"
      sx={{ display: 'block', width: '100%', height: '100%' }}
    >
      <Card sx={{ height: '100%', '&:hover': { boxShadow: theme.shadows[4] } }}>
        <CardActionArea component={Link} to={`/tax-binders/${binder.id}`}>
          {/* The unread Badge sits outside the link, so surface the count inside it
              for screen readers — this keeps it part of the link's accessible name. */}
          {unread > 0 && <Box component="span" sx={visuallyHidden}>{`${unread} unread messages`}</Box>}
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
              <Chip
                icon={<TaxIcon />}
                label={SERVICE_LINE_CONFIG[ServiceLine.Tax].label}
                size="small"
                color={SERVICE_LINE_CONFIG[ServiceLine.Tax].color}
                variant="outlined"
              />
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <TaxBinderTypeChip type={binder.type} />
                <Chip label={binder.taxYear || 'N/A'} color="primary" size="small" />
              </Box>
            </Box>

            <Typography variant="h6" component="div">
              {binder.name || 'Untitled Binder'}
            </Typography>

            <Typography variant="body2" color="text.secondary" gutterBottom>
              {binder.preparerName ? 'Assigned Preparer: ATA' : 'Managed by: ATA'}
            </Typography>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
              <Chip
                label={getClientStageLabel(binder.stage, binder.isAmendmentInProgress)}
                size="small"
                color={getClientStageColor(binder.stage, binder.isAmendmentInProgress)}
              />
              {hasOutstandingSignatureHints(binder) && (
                <Chip icon={<DrawIcon />} label="Signature Required" size="small" color="error" variant="outlined" />
              )}
            </Stack>
          </CardContent>
          <Box sx={{ px: 2, pb: 2, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2" color="primary">
              Open Binder
            </Typography>
            <ArrowForwardIcon fontSize="small" color="primary" />
          </Box>
        </CardActionArea>
      </Card>
    </Badge>
  );
}
